// Modal composer for elevating a regular user's bug report into the
// dev-sync queue. Opened from the per-row "Elevate to dev sync" button on
// DevPanelBugReportsSection. See docs/planning/bug_report_elevation.md.

import { useEffect, useMemo, useState } from "react";
import type { BugReport } from "./devPanelTypes";

const REPO_PATH_PATTERN = /^(public\/|src\/assets\/)[a-zA-Z0-9_./-]+\.(svg|png|webp|jpe?g)$/i;

export type ElevateModalSubmitArgs = {
  body: string;
  reviewerNote: string | null;
  placement: { repo_path: string } | null;
};

type Props = {
  report: BugReport;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (args: ElevateModalSubmitArgs) => void | Promise<void>;
};

export function DevPanelElevateBugModal({ report, busy, error, onClose, onSubmit }: Props) {
  const [body, setBody] = useState(report.description ?? "");
  const [reviewerNote, setReviewerNote] = useState("");
  const [repoPath, setRepoPath] = useState("");

  // Reset composer state whenever the modal is opened against a fresh report.
  useEffect(() => {
    setBody(report.description ?? "");
    setReviewerNote("");
    setRepoPath("");
  }, [report.id, report.description]);

  // Close on Escape — matches the dock/panel keyboard convention.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const repoPathTrimmed = repoPath.trim();
  const repoPathInvalid = repoPathTrimmed.length > 0 && !REPO_PATH_PATTERN.test(repoPathTrimmed);
  const submitDisabled = busy || body.trim().length === 0 || repoPathInvalid;

  const trustHint = useMemo(() => {
    return "Trust tier on the elevated note follows YOUR roles, not the reporter's. " +
      "platform_owner → owner_direct (exports immediately). " +
      "platform_dev → needs_review (owner Approve/Alter/Deny gate still applies).";
  }, []);

  function handleSubmit() {
    if (submitDisabled) return;
    void onSubmit({
      body: body.trim(),
      reviewerNote: reviewerNote.trim() ? reviewerNote.trim() : null,
      placement: repoPathTrimmed ? { repo_path: repoPathTrimmed } : null,
    });
  }

  return (
    <div
      data-elevate-bug-modal="1"
      className="fixed inset-0 z-[9300] flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="mt-12 w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-xl">
        <header className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Elevate bug report to dev sync queue</h2>
            <p className="mt-1 text-xs text-gray-500">
              Creates a linked <code>dev_actionable_notes</code> row. Original{" "}
              <code>bug_reports</code> row is preserved and auto-flips to{" "}
              <code>in_progress</code>. When the dev note is sync-completed, the
              bug report becomes <code>resolved</code>.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
        </header>

        <section className="space-y-4 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Reporter said</div>
            <div className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              {report.description || <em className="text-gray-400">No description</em>}
            </div>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-gray-500">
              {report.mode ? <span>mode: {report.mode}</span> : null}
              {report.version ? <span>v{report.version}</span> : null}
              {report.route_path ? <span className="break-all">route: {report.route_path}</span> : null}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="elevate-body">
              IDE-actionable body
            </label>
            <p className="text-[11px] text-gray-500">
              Edit freely. Original description above is preserved on the bug_reports row.
            </p>
            <textarea
              id="elevate-body"
              className="mt-1 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm"
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="elevate-repopath">
                Repo target path (optional)
              </label>
              <p className="text-[11px] text-gray-500">
                For graphic placements only — <code>public/…</code> or <code>src/assets/…</code> ending in .svg/.png/.webp/.jpg/.jpeg.
              </p>
              <input
                id="elevate-repopath"
                className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                  repoPathInvalid ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
                placeholder="src/assets/icons/foo.svg"
                value={repoPath}
                onChange={(e) => setRepoPath(e.target.value)}
                disabled={busy}
              />
              {repoPathInvalid ? (
                <p className="mt-1 text-[11px] text-red-700">
                  Path must start with <code>public/</code> or <code>src/assets/</code> and end in .svg/.png/.webp/.jpg/.jpeg.
                </p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="elevate-reviewer-note">
                Reviewer note (optional)
              </label>
              <p className="text-[11px] text-gray-500">Why you're elevating, gotchas for the agent, etc.</p>
              <textarea
                id="elevate-reviewer-note"
                className="mt-1 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm"
                rows={3}
                value={reviewerNote}
                onChange={(e) => setReviewerNote(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <p className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] text-indigo-900">
            {trustHint}
          </p>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</div>
          ) : null}
        </section>

        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {busy ? "Elevating…" : "Elevate to dev sync"}
          </button>
        </footer>
      </div>
    </div>
  );
}
